import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createIdea, getIdeaById, updateIdea, updateIdeaStatus } from '../../services/idea.service';
import { showSuccess, showError } from '../../services/notification.service';
import RoleViewTabs from './shared/RoleViewTabs';
import './IdeaSubmission.css';

const CATEGORIES = [
  { value: 'HW', label: 'Hardware (HW)' },
  { value: 'HW_SW', label: 'Hardware + Software (HW/SW)' },
  { value: 'SERVICES_REVENUE', label: 'Services – Revenue Generating' },
  { value: 'SERVICES_ENABLING', label: 'Services – Enabling' },
];

const QUESTIONS = [
  { id: 'q1', question: '1. What problem does your idea solve?', placeholder: 'Describe the core problem your solution addresses...', required: true },
  { id: 'q2', question: '2. Who is your target audience / customer?', placeholder: 'Define your primary customer segments...', required: true },
  { id: 'q3', question: '3. What makes your solution unique?', placeholder: 'Describe your competitive advantage and differentiation...', required: true },
  { id: 'q4', question: '4. What is your revenue model?', placeholder: 'Explain how you plan to generate revenue...', required: false },
  { id: 'q5', question: '5. What are the key technical requirements?', placeholder: 'List main technologies, infrastructure, and technical dependencies...', required: false },
  { id: 'q6', question: '6. What is your go-to-market strategy?', placeholder: 'Describe how you plan to reach and acquire customers...', required: false },
  { id: 'q7', question: '7. What are the major risks and challenges?', placeholder: 'Identify potential obstacles and how you plan to address them...', required: false },
  { id: 'q8', question: '8. What is your expected timeline and budget?', placeholder: 'Provide estimates for development time and financial requirements...', required: false },
];

const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

function IdeaSubmission() {
  const navigate = useNavigate();
  const { ideaId } = useParams(); // set when editing an existing draft

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(!!ideaId);
  const [draftIdeaId, setDraftIdeaId] = useState('');

  // Load existing idea when editing
  useEffect(() => {
    if (!ideaId) return;
    const load = async () => {
      try {
        const data = await getIdeaById(ideaId);
        if (data.success && data.idea) {
          const { idea } = data;
          setTitle(idea.title || '');
          setDescription(idea.description || '');
          setCategory(idea.category || '');
          setTags(idea.tags || []);
          setAnswers(idea.questionnaire_data || {});
        }
      } catch (e) {
        showError('Failed to load idea');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [ideaId]);

  // Auto-save draft
  const saveDraft = useCallback(async () => {
    if (!title.trim()) return;
    setIsSaving(true);
    try {
      const payload = buildPayload(true);
      const activeIdeaId = ideaId || draftIdeaId;
      if (activeIdeaId) {
        await updateIdea(activeIdeaId, payload);
      } else {
        const res = await createIdea(payload);
        if (res?.idea_id) setDraftIdeaId(res.idea_id);
      }
      setLastSaved(new Date());
    } catch {
      // Silent — auto-save failures are non-critical
    } finally {
      setIsSaving(false);
    }
  }, [title, description, category, tags, answers, ideaId, draftIdeaId]); // eslint-disable-line

  useEffect(() => {
    const timer = setInterval(saveDraft, AUTO_SAVE_INTERVAL);
    return () => clearInterval(timer);
  }, [saveDraft]);

  const buildPayload = (isDraft = false) => ({
    title: title.trim(),
    description: description.trim(),
    category,
    tags,
    questionnaire_data: answers,
    is_draft: isDraft,
  });

  const validateBasicFields = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (!category) errs.category = 'Please select a category';
    return errs;
  };

  const validateCurrentQuestion = () => {
    const q = QUESTIONS[currentStep];
    if (q.required && !answers[q.id]?.trim()) {
      setErrors(prev => ({ ...prev, [q.id]: 'This answer is required' }));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentQuestion()) {
      setErrors(prev => { const n = { ...prev }; delete n[QUESTIONS[currentStep].id]; return n; });
      if (currentStep < QUESTIONS.length - 1) setCurrentStep(s => s + 1);
    }
  };

  const handlePrev = () => { if (currentStep > 0) setCurrentStep(s => s - 1); };

  const handleAddTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 10) {
      setTags(prev => [...prev, t]);
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const payload = buildPayload(true);
      const activeIdeaId = ideaId || draftIdeaId;
      if (activeIdeaId) {
        await updateIdea(activeIdeaId, payload);
      } else {
        const res = await createIdea(payload);
        if (res?.idea_id) setDraftIdeaId(res.idea_id);
      }
      showSuccess('Draft saved');
      setLastSaved(new Date());
    } catch {
      showError('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    const basicErrors = validateBasicFields();
    // Validate required questions
    for (const q of QUESTIONS.filter(q => q.required)) {
      if (!answers[q.id]?.trim()) {
        basicErrors[q.id] = 'This answer is required';
      }
    }
    if (Object.keys(basicErrors).length > 0) {
      setErrors(basicErrors);
      // Jump to first question with an error
      const firstErrIdx = QUESTIONS.findIndex(q => basicErrors[q.id]);
      if (firstErrIdx >= 0) setCurrentStep(firstErrIdx);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildPayload(false);
      const activeIdeaId = ideaId || draftIdeaId;
      if (activeIdeaId) {
        await updateIdea(activeIdeaId, payload);
        await updateIdeaStatus(activeIdeaId, { status: 'SUBMITTED' });
      } else {
        await createIdea(payload);
      }
      showSuccess('Idea submitted successfully!');
      navigate('/ideas');
    } catch (e) {
      showError(e?.response?.data?.error || 'Failed to submit idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="idea-submission"><p>Loading...</p></div>;
  }

  const currentQ = QUESTIONS[currentStep];
  const answeredCount = QUESTIONS.filter(q => answers[q.id]?.trim()).length;

  return (
    <div className="idea-submission">
      <RoleViewTabs />

      <h2>{ideaId ? 'Edit Idea' : 'Submit a New Idea'}</h2>
      <p className="subtitle">
        Complete the form below. Fields marked <span style={{ color: '#ef4444' }}>*</span> are required.
        {lastSaved && <span style={{ marginLeft: 12, color: '#10b981', fontSize: '0.78rem' }}>
          ✓ Saved {lastSaved.toLocaleTimeString()}
        </span>}
        {isSaving && <span style={{ marginLeft: 12, color: '#6b7280', fontSize: '0.78rem' }}>Saving…</span>}
      </p>

      {/* Basic info */}
      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-group">
          <label>Idea Title <span className="required">*</span></label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Give your idea a concise, descriptive title"
            maxLength={150}
          />
          <div className="char-count">{title.length}/150</div>
          {errors.title && <div className="error-msg">{errors.title}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Category <span className="required">*</span></label>
            <select value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">Select category…</option>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {errors.category && <div className="error-msg">{errors.category}</div>}
          </div>

          <div className="form-group">
            <label>Tags (up to 10)</label>
            <div className="tag-input-row">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag…"
                maxLength={30}
              />
              <button onClick={handleAddTag} type="button">Add</button>
            </div>
            {tags.length > 0 && (
              <div className="tags-container">
                {tags.map(tag => (
                  <span key={tag} className="tag-chip">
                    {tag}
                    <button onClick={() => setTags(prev => prev.filter(t => t !== tag))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Brief Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="A short summary of your idea (optional — the 8 questions below capture the full picture)"
            maxLength={500}
            rows={3}
          />
          <div className="char-count">{description.length}/500</div>
        </div>
      </div>

      {/* 8Q Questionnaire */}
      <div className="form-section">
        <h3>Idea Questionnaire ({answeredCount}/{QUESTIONS.length} answered)</h3>

        <div className="question-stepper">
          {QUESTIONS.map((q, i) => (
            <div
              key={q.id}
              className={`step-dot ${i === currentStep ? 'active' : ''} ${answers[q.id]?.trim() ? 'answered' : ''}`}
              onClick={() => setCurrentStep(i)}
              title={`Q${i + 1}`}
            >
              {i + 1}
            </div>
          ))}
        </div>

        <div className="form-group">
          <label>
            {currentQ.question}
            {currentQ.required && <span className="required"> *</span>}
          </label>
          <textarea
            key={currentQ.id}
            value={answers[currentQ.id] || ''}
            onChange={e => {
              setAnswers(prev => ({ ...prev, [currentQ.id]: e.target.value }));
              if (errors[currentQ.id]) setErrors(prev => { const n = { ...prev }; delete n[currentQ.id]; return n; });
            }}
            placeholder={currentQ.placeholder}
            rows={5}
          />
          {errors[currentQ.id] && <div className="error-msg">{errors[currentQ.id]}</div>}
        </div>

        <div className="step-nav">
          <button className="btn-secondary" onClick={handlePrev} disabled={currentStep === 0}>
            ← Previous
          </button>
          <span className="step-progress">Question {currentStep + 1} of {QUESTIONS.length}</span>
          {currentStep < QUESTIONS.length - 1 ? (
            <button className="btn-primary" onClick={handleNext}>Next →</button>
          ) : (
            <span style={{ width: 90 }} />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="action-bar">
        <button className="btn-secondary" onClick={() => navigate('/ideas')}>Cancel</button>
        <button className="btn-draft" onClick={handleSaveDraft} disabled={isSaving}>
          {isSaving ? 'Saving…' : '💾 Save Draft'}
        </button>
        <button className="btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : '🚀 Submit Idea'}
        </button>
      </div>
    </div>
  );
}

export default IdeaSubmission;
